import { Link, Stack, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { WarningAmberRounded as WarningIcon } from "@mui/icons-material";
import { HintIcon } from "@/components/ui/mui/HintIcon";
import { HELD_REASON_TEXT, type ParsedLoadWarnings } from "./parseLoadWarnings";

/**
 * One line, not a banner: how many claimants carry a held-spend or skipped-
 * totals warning, with the reason behind a hint. The rows themselves say which
 * (see buildAttentionMap). Renders nothing for a clean load.
 */
/**
 * The hint behind the notice: one sentence per held reason actually present (a
 * review hold reads differently from a concurrent insert), the fence-skip rule
 * when any claimant was skipped, then any other warning verbatim.
 */
export const buildWarningHint = (parsed: ParsedLoadWarnings, reasonFacetAvailable = true): string => {
  const heldReasons = [...new Set(parsed.heldSpend.map((h) => h.reason ?? "legacy"))];
  return [
    ...heldReasons.map((r) => HELD_REASON_TEXT[r] ?? HELD_REASON_TEXT.unknown),
    parsed.fenceSkipped.length > 0
      ? "A load that cannot fence a claimant within its wait budget skips that claimant's post-processing; the next load or recompute catches up."
      : null,
    parsed.noPaidDate
      ? `Lines with a paid amount and no paid date do not count toward the deductible until a paid date is mapped and the document is reloaded, or each line's Paid Date is set by hand.${
          reasonFacetAvailable
            ? " Find them under Claim Items → Reason → Missing paid date once the claimants' totals have refreshed."
            : ""
        }`
      : null,
    ...parsed.other,
  ]
    .filter(Boolean)
    .join(" ");
};

interface LoadWarningNoticeProps {
  parsed: ParsedLoadWarnings;
  /**
   * Route to the claim-lines list filtered to Missing paid date for this
   * document's client (#7380). Shown as a quiet link after the counts when the
   * load wrote such lines; omitted when the viewer may not list claim lines.
   */
  noPaidDateHref?: string | null;
  /**
   * False when the deductible-basis flag is off: the legacy stamp never writes
   * `missing_paid_date`, so the hint must not send the reader to a facet that
   * would come back empty. Default true.
   */
  reasonFacetAvailable?: boolean;
}

export const LoadWarningNotice = ({ parsed, noPaidDateHref, reasonFacetAvailable = true }: LoadWarningNoticeProps) => {
  const held = parsed.heldSpend.length;
  const skipped = parsed.fenceSkipped.length;
  const noDate = parsed.noPaidDate?.lines ?? 0;
  const other = parsed.other;
  if (held === 0 && skipped === 0 && noDate === 0 && other.length === 0) return null;
  const parts: string[] = [];
  if (held > 0) parts.push(`Stop-Loss spend held for ${held} ${held === 1 ? "claimant" : "claimants"}`);
  if (skipped > 0) parts.push(`totals not updated for ${skipped} ${skipped === 1 ? "claimant" : "claimants"}`);
  if (noDate > 0)
    parts.push(`${noDate.toLocaleString()} ${noDate === 1 ? "line" : "lines"} loaded without a paid date`);
  // Held spend and skipped totals are marked on the claimant rows; the paid-date
  // count is a document-level fact, so the suffix only applies to the first two.
  const markedOnRows = held > 0 || skipped > 0;
  const hint = buildWarningHint(parsed, reasonFacetAvailable);
  return (
    <Stack direction="row" alignItems="center" columnGap={0.75} data-testid="load-warning-notice">
      <WarningIcon sx={{ fontSize: 16, color: "warning.main" }} />
      <Typography variant="caption" sx={{ color: "warning.dark", fontWeight: 600 }}>
        {parts.length > 0
          ? parts.join(" · ")
          : `${other.length} ${other.length === 1 ? "warning" : "warnings"} on this load`}
      </Typography>
      {markedOnRows && (
        <Typography variant="caption" color="text.secondary">
          · marked on the rows
        </Typography>
      )}
      {noDate > 0 && reasonFacetAvailable && noPaidDateHref && (
        <Link
          component={RouterLink}
          to={noPaidDateHref}
          variant="caption"
          underline="hover"
          sx={{ fontWeight: 600 }}
          aria-label="Open Claim Items filtered to lines missing a paid date"
        >
          Open in Claim Items
        </Link>
      )}
      {hint && <HintIcon hint={hint} />}
    </Stack>
  );
};
